/* eslint-disable class-methods-use-this */
import { create } from '@civic/prove-icp-principal';
import { ChainError, ChainType, prefixLogger, SignatureMethod, ErrorCode, } from '@civic/gateway-client-core';
import { getToken } from './gateway/gatewayUtils';
import { CIVIC_DERIVATION_ORIGIN, CIVIC_ISSUER_DEV, CIVIC_ISSUER_PROD } from './config';
export const DEFAULT_CHAIN_POLLING_INTERVAL_MS = 1000;
const generatePollingInterval = (pollingInterval) => Math.max(pollingInterval || DEFAULT_CHAIN_POLLING_INTERVAL_MS, DEFAULT_CHAIN_POLLING_INTERVAL_MS);
const logDebug = prefixLogger('ICP chain implementation').debug;
export class ICPChainImplementation {
    constructor(inputs) {
        this.inputs = inputs;
        this.listeners = [];
        this.pollTimerRef = null;
        const { network, chainPollingIntervalMs } = inputs;
        if (chainPollingIntervalMs && chainPollingIntervalMs < DEFAULT_CHAIN_POLLING_INTERVAL_MS) {
            // eslint-disable-next-line no-console
            console.warn(`chainPollingIntervalMs was set to less than ${DEFAULT_CHAIN_POLLING_INTERVAL_MS}ms, which is not allowed. It's clamped to ${DEFAULT_CHAIN_POLLING_INTERVAL_MS}ms.`);
        }
        this.chainDetails = {
            chainType: ChainType.ICP,
            chainNetwork: network.name,
        };
    }
    async findGatewayToken() {
        const { wallet, gatekeeperNetwork } = this.inputs;
        if (!wallet.principal) {
            return undefined;
        }
        const issuer = this.inputs.stage === 'dev' ? CIVIC_ISSUER_DEV : CIVIC_ISSUER_PROD;
        const onChainToken = await getToken(wallet.principal, gatekeeperNetwork, issuer);
        logDebug('findGatewayToken', onChainToken);
        return onChainToken || undefined;
    }
    async proveWalletOwnership(message) {
        if (!message) {
            throw new Error('Need to pass a message to prove wallet ownership');
        }
        try {
            const proof = await create({ message, derivationOrigin: CIVIC_DERIVATION_ORIGIN });
            logDebug('proveWalletOwnership', proof);
            return { proof, signatureMethod: SignatureMethod.MESSAGE };
        }
        catch (error) {
            logDebug('handleUserSignedTransaction', { error });
            throw new ChainError('ICP prove wallet ownership error', ErrorCode.POWO_ERROR);
        }
    }
    async signMessage() {
        throw new ChainError('Not supported');
    }
    async ownerDids() {
        return [`did:icp:v0:${this.inputs.owner}`];
    }
    // Listeners:
    on(event, tokenDidChange) {
        logDebug('add ICP token listener...', tokenDidChange);
        const listener = this.getListenerForOwner(this.inputs.owner, (token) => {
            if (event === 'TOKEN_CREATED' && !!this.gatewayToken) {
                return;
            }
            this.gatewayToken = token;
            tokenDidChange(token);
        });
        this.listeners.push({ unsubscribe: () => this.removeOnGatewayTokenChangeListener(listener) });
        return this;
    }
    getListenerForOwner(_inOwner, tokenDidChange) {
        if (this.pollTimerRef) {
            clearInterval(this.pollTimerRef);
        }
        this.pollTimerRef = setInterval(async () => {
            const token = await this.findGatewayToken();
            if (token) {
                tokenDidChange(token);
            }
        }, generatePollingInterval(this.inputs.chainPollingIntervalMs));
        return this.pollTimerRef;
    }
    removeOnGatewayTokenChangeListener(listenerId) {
        logDebug('removeOnGatewayTokenChangeListener listenerId', { listenerId, listenersLength: this.listeners.length });
        clearInterval(listenerId);
        this.listeners = this.listeners.filter((listener) => listener.unsubscribe !== this.removeOnGatewayTokenChangeListener.bind(this, listenerId));
    }
    async onDestroy() {
        this.listeners.forEach((listener) => listener !== undefined && listener.unsubscribe());
        this.listeners = [];
        if (this.pollTimerRef) {
            clearInterval(this.pollTimerRef);
        }
    }
}
